import React, { useState, useEffect } from 'react';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { X, Loader } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SquarePaymentModal = ({ isOpen, onClose, amount, onSuccess, appointmentId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationId, setApplicationId] = useState('');
  const [locationId, setLocationId] = useState('');

  useEffect(() => {
    if (isOpen) {
      initializeSquare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const initializeSquare = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get Square configuration
      const token = sessionStorage.getItem('token');
      const configResponse = await axios.get(`${API_URL}/payment-settings/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const squareProvider = configResponse.data.providers?.find(p => p.provider === 'square');
      
      if (!squareProvider || !squareProvider.public_key) {
        throw new Error('Square is not configured');
      }

      setApplicationId(squareProvider.public_key);
      setLocationId(squareProvider.square_location_id || 'main');
      setLoading(false);

    } catch (err) {
      console.error('Initialize Square error:', err);
      setError(err.response?.data?.error || 'Failed to initialize Square');
      setLoading(false);
    }
  };

  const handlePayment = async (token) => {
    try {
      const authToken = sessionStorage.getItem('token');
      
      // Create payment
      const response = await axios.post(
        `${API_URL}/payments/create-square-payment`,
        {
          sourceId: token.token,
          amount,
          appointmentId
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.paymentId) {
        onSuccess(response.data.paymentId);
        onClose();
      } else {
        setError('Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.error || 'Payment failed. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#000000">
              <path d="M4.01 8.54C4.01 6.58 5.6 5 7.57 5h8.86c1.97 0 3.56 1.58 3.56 3.54v6.92c0 1.96-1.59 3.54-3.56 3.54H7.57c-1.97 0-3.56-1.58-3.56-3.54V8.54z"/>
            </svg>
            <h2 className="text-xl font-bold">Square Payment</h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <div className="text-lg font-semibold text-gray-900 mb-2">
              Amount to pay: ${amount.toFixed(2)}
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="animate-spin text-primary-600 mb-4" size={32} />
              <p className="text-gray-600">Loading Square...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 mb-4">
              <p className="font-semibold mb-2">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && applicationId && (
            <PaymentForm
              applicationId={applicationId}
              locationId={locationId}
              cardTokenizeResponseReceived={handlePayment}
              createPaymentRequest={() => ({
                countryCode: 'US',
                currencyCode: 'USD',
                total: {
                  amount: amount.toFixed(2),
                  label: 'Total',
                },
              })}
            >
              <CreditCard
                buttonProps={{
                  css: {
                    backgroundColor: '#006aff',
                    fontSize: '16px',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#005ce6',
                    },
                  },
                }}
              />
            </PaymentForm>
          )}

          {error && (
            <button
              onClick={onClose}
              className="mt-4 btn btn-secondary w-full"
            >
              Close
            </button>
          )}

          {!loading && !error && (
            <p className="text-xs text-gray-500 mt-4 text-center">
              Your payment is processed securely by Square.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SquarePaymentModal;
